import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ReportConfigDto, ErrorResponse } from '@thingslog/repositories';
import { useToast } from '@thingslog/ui-components';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import { scheduledReportsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import { ReportConfigForm } from './forms';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';

const CreateScheduledReportConfig: FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const selectedCompanyId = useDetermineCompanyId();

  const { useCreateReportConfig } = scheduledReportsQueryClient;
  const { mutate: createConfig, isLoading } = useCreateReportConfig();

  const handleSubmit = (data: ReportConfigDto): void => {
    const configData = {
      ...data,
      companyId: selectedCompanyId || 0,
    };

    createConfig(configData, {
      onSuccess: () => {
        toast({
          type: 'success',
          message: t('report_config_create_success'),
          duration: 3000,
        });
        navigate('/app/ScheduledReportsConfig');
      },
      onError: (error: AxiosError<ErrorResponse>) => {
        toast({
          type: 'error',
          message: error.response?.data?.message || t('report_config_create_error'),
          duration: 5000,
        });
      },
    });
  };

  const handleCancel = (): void => {
    navigate('/app/ScheduledReportsConfig');
  };

  if (selectedCompanyId === null) {
    return (
      <Header>
        <Container>
          <div className="text-2xl m-auto mt-20 text-center">
            {t('report_configs_select_company_message')}
          </div>
        </Container>
      </Header>
    );
  }

  return (
    <Header>
      <Container>
        <section className="flex justify-center mb-6">
          <TitleHeader title={t('report_config_create_header')} />
        </section>
        <section className="max-w-2xl mx-auto">
          <ReportConfigForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isLoading}
            defaultValues={{ companyId: selectedCompanyId }}
          />
        </section>
      </Container>
    </Header>
  );
};

export default CreateScheduledReportConfig;
